import { Link } from "react-router-dom";
import {
	ArrowUpRight,
	Bike,
	BookOpen,
	Dumbbell,
	Flame,
	Footprints,
	Heart,
	MapPin,
	PenLine,
	Timer,
} from "lucide-react";
import { useLanguage } from "../../../contexts/LanguageContext";
import { getDateLocale } from "../../../lib/utils";
import { ExerciseRow } from "./ExerciseRow";

const ENTRY_TYPE_ICONS = {
	running: Footprints,
	cycling: Bike,
	strength: Dumbbell,
	cardio: Heart,
};

const normalize = (value) =>
	String(value || "")
		.trim()
		.toLowerCase();

const shiftDate = (dateStr, days) => {
	const d = new Date(`${dateStr}T12:00:00`);
	d.setDate(d.getDate() + days);
	return [
		d.getFullYear(),
		String(d.getMonth() + 1).padStart(2, "0"),
		String(d.getDate()).padStart(2, "0"),
	].join("-");
};

function matchesFocus(focus, group, exercise) {
	if (!focus) return false;
	if (focus.programId && focus.programId !== group.programId) return false;
	if (focus.dayId && focus.dayId !== group.dayId) return false;
	if (
		focus.exerciseId &&
		String(exercise.result?.exercise_id || exercise.exercise_id || "").trim() ===
			focus.exerciseId
	)
		return true;
	return Boolean(
		focus.exerciseName &&
			normalize(exercise.exercise_name) === normalize(focus.exerciseName),
	);
}

/**
 * Un giorno dello storico del Diario: data, poi un blocco per ogni
 * allenamento di programma registrato, i risultati inseriti a mano e le
 * note libere. Solo ciò che è stato fatto.
 *
 * Props:
 *   - day: da `buildHistoryDay`
 *   - todayStr: "YYYY-MM-DD"
 *   - workoutHref: (group) → href della Dashboard o null
 *   - focusTarget: deep link `diaryFocus` già filtrato su questa data
 *   - onDeleteResult: (exercise) → void, per i risultati eliminabili
 *   - onEditResult: (exercise) → void, per i risultati correggibili
 */
export function HistoryDay({
	day,
	todayStr,
	workoutHref,
	focusTarget,
	onDeleteResult,
	onEditResult,
}) {
	const { t, language } = useLanguage();
	const dateLabel = new Date(`${day.date}T12:00:00`).toLocaleDateString(
		getDateLocale(language),
		{ weekday: "long", day: "numeric", month: "long" },
	);
	const relativeLabel =
		day.date === todayStr
			? t("today")
			: day.date === shiftDate(todayStr, -1)
				? t("yesterday")
				: null;

	return (
		<section className="space-y-2" data-testid="diary-history-day">
			<div className="flex items-center gap-2 px-1">
				<h2 className="text-xs uppercase tracking-wider font-heading text-muted-foreground capitalize">
					{dateLabel}
				</h2>
				{relativeLabel && (
					<span className="text-[10px] bg-volt/20 text-volt px-1.5 py-0.5 rounded-sm uppercase tracking-wider font-medium">
						{relativeLabel}
					</span>
				)}
			</div>

			{day.groups.map((group) => {
				const href = workoutHref?.(group);
				return (
					<div
						key={group.key}
						className="bg-card border border-border rounded-sm overflow-hidden"
					>
						<div
							className={`px-3 py-2 border-b flex items-center gap-2 ${
								group.color
									? `${group.color.bgTint} ${group.color.border}`
									: "bg-secondary/40 border-border"
							}`}
						>
							<span
								className={`inline-block w-1 h-5 rounded-full flex-shrink-0 ${
									group.color ? group.color.dot : "bg-acid"
								}`}
							/>
							<div className="flex-1 min-w-0">
								<div
									className={`text-xs uppercase tracking-wider font-heading truncate ${
										group.color ? group.color.text : "text-acid"
									}`}
								>
									{group.isManual
										? t("diaryHistoryManualGroup")
										: group.programTitle || t("diaryHistoryProgramFallback")}
								</div>
								{group.dayTitle && (
									<div className="text-[11px] text-muted-foreground truncate">
										{group.dayTitle}
									</div>
								)}
							</div>
							{href && (
								<Link
									to={href}
									className={`flex-shrink-0 inline-flex items-center gap-1 min-h-11 -my-2 px-1 text-[11px] font-medium ${group.color?.text || "text-volt"} hover:underline`}
								>
									{t("diaryHistoryOpenWorkout")}
									<ArrowUpRight className="w-3.5 h-3.5" />
								</Link>
							)}
						</div>
						<div>
							{group.items.map((exercise) => (
								<ExerciseRow
									key={exercise._uid}
									exercise={exercise}
									language={language}
									autoOpen={matchesFocus(focusTarget, group, exercise)}
									onDelete={
										onDeleteResult && exercise.canDelete
											? () => onDeleteResult(exercise)
											: undefined
									}
									onEdit={
										onEditResult && exercise.canEdit
											? () => onEditResult(exercise)
											: undefined
									}
								/>
							))}
						</div>
					</div>
				);
			})}

			{day.entries.length > 0 && (
				<div className="bg-card border border-border rounded-sm overflow-hidden">
					<div className="px-3 py-2 border-b border-border bg-secondary/40 flex items-center gap-2">
						<PenLine className="w-3.5 h-3.5 text-muted-foreground" />
						<span className="text-xs uppercase tracking-wider font-heading text-muted-foreground">
							{t("diaryHistoryFreeEntries")}
						</span>
					</div>
					{day.entries.map((entry) => (
						<DiaryEntryRow key={entry.entry_id} entry={entry} />
					))}
				</div>
			)}
		</section>
	);
}

// Le voci libere del vecchio Diario: titolo, descrizione e i numeri inseriti.
function DiaryEntryRow({ entry }) {
	const TypeIcon = ENTRY_TYPE_ICONS[entry.workout_type] || BookOpen;
	const metrics = [
		entry.duration_minutes && {
			Icon: Timer,
			label: `${entry.duration_minutes} min`,
		},
		entry.distance_km && { Icon: MapPin, label: `${entry.distance_km} km` },
		entry.sets &&
			entry.reps && {
				Icon: Dumbbell,
				label: `${entry.sets}x${entry.reps}${entry.weight_kg ? ` @ ${entry.weight_kg}kg` : ""}`,
			},
		entry.avg_heart_rate && {
			Icon: Heart,
			label: `${entry.avg_heart_rate} bpm`,
		},
		entry.calories && { Icon: Flame, label: `${entry.calories} kcal` },
	].filter(Boolean);

	return (
		<div className="px-3 py-2.5 border-b border-border/40 last:border-b-0 flex items-start gap-3">
			<TypeIcon className="w-4 h-4 mt-0.5 text-acid flex-shrink-0" />
			<div className="flex-1 min-w-0 space-y-1">
				<p className="text-sm font-medium truncate">{entry.title}</p>
				{entry.description && (
					<p className="text-xs text-muted-foreground">{entry.description}</p>
				)}
				{metrics.length > 0 && (
					<div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
						{metrics.map(({ Icon, label }) => (
							<span key={label} className="inline-flex items-center gap-1">
								<Icon className="w-3.5 h-3.5" />
								{label}
							</span>
						))}
					</div>
				)}
				{entry.notes && (
					<p className="text-xs text-muted-foreground italic">{entry.notes}</p>
				)}
			</div>
		</div>
	);
}
